import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '@/shared/utils/supabase'
import { useAuth } from '@/modules/auth'
import { PageLayout } from '@/ui/layout'
import { Card, CardContent, CardHeader, CardTitle, CardDescription, WaveLoader } from '@/ui/components'

type CallbackState = 'pending' | 'success' | 'error'

export default function AuthCallback() {
  const navigate = useNavigate()
  const { completeEmailVerification } = useAuth()
  const [status, setStatus] = useState<CallbackState>('pending')
  const [errorMessage, setErrorMessage] = useState('Something went wrong during sign in.')

  useEffect(() => {
    const handleCallback = async () => {
      try {
        // Case 1: PKCE flow — Supabase returns a ?code= query param.
        const urlParams = new URLSearchParams(window.location.search)
        const code = urlParams.get('code')

        if (code) {
          const { data, error } = await supabase.auth.exchangeCodeForSession(code)
          if (error || !data.session) {
            throw new Error(error?.message ?? 'Failed to exchange authorization code.')
          }
          await finalize(data.session, data.session.user)
          return
        }

        // Case 2: Implicit flow — Supabase returns tokens in the URL hash.
        const hash = window.location.hash
        if (hash) {
          const hashParams = new URLSearchParams(hash.substring(1))
          const accessToken = hashParams.get('access_token')
          const refreshToken = hashParams.get('refresh_token')
          const errorParam = hashParams.get('error')
          const errorDescription = hashParams.get('error_description')

          if (errorParam) {
            throw new Error(errorDescription ? decodeURIComponent(errorDescription) : errorParam)
          }

          if (accessToken && refreshToken) {
            const { data, error } = await supabase.auth.setSession({ access_token: accessToken, refresh_token: refreshToken })
            if (error || !data.session) {
              throw new Error(error?.message ?? 'Failed to create session from tokens.')
            }
            await finalize(data.session, data.session.user)
            return
          }
        }

        // Case 3: Supabase may have already processed the session (detectSessionInUrl path).
        const { data: existing } = await supabase.auth.getSession()
        if (existing.session) {
          await finalize(existing.session, existing.session.user)
          return
        }

        throw new Error('No authorization data found in the callback URL.')
      } catch (err) {
        setErrorMessage(err instanceof Error ? err.message : 'Authentication failed.')
        setStatus('error')
      }
    }

    const finalize = async (session: any, user: any) => {
      // Sync tokens into our localStorage keys and give the session to AuthProvider.
      // completeEmailVerification returns the backend profile, which carries the
      // server-side onboarding flag.
      const profile = await completeEmailVerification(session, user)

      // Clean up the URL so tokens don't linger in browser history.
      window.history.replaceState(null, '', window.location.pathname)

      setStatus('success')

      // Prefer redirect param (e.g. from share link join flow); otherwise send
      // to onboarding if the server-side flag is null, else to the dashboard.
      const redirectParam = new URLSearchParams(window.location.search).get('redirect')
      const isValidRedirect = redirectParam && redirectParam.startsWith('/') && !redirectParam.startsWith('//')
      const destination = isValidRedirect
        ? redirectParam
        : profile?.onboarding_completed_at
          ? '/dashboard'
          : '/onboarding'
      setTimeout(() => navigate(destination, { replace: true }), 800)
    }

    handleCallback()
  }, [navigate, completeEmailVerification])

  return (
    <PageLayout showHeader={false}>
      <div className="min-h-screen flex items-center justify-center px-6">
        <div className="w-full max-w-md">
          <Card>
            <CardHeader>
              <CardTitle>Signing you in</CardTitle>
              <CardDescription>Completing your Google sign in.</CardDescription>
            </CardHeader>
            <CardContent>
              {status === 'pending' && (
                <div className="flex flex-col items-center space-y-4 py-8 text-center">
                  <WaveLoader />
                  <p className="text-sm text-muted-foreground">Just a moment…</p>
                </div>
              )}
              {status === 'success' && (
                <div className="flex flex-col items-center space-y-4 py-8 text-center">
                  <div className="text-4xl">✅</div>
                  <p className="text-base font-medium text-foreground">Signed in successfully</p>
                  <p className="text-sm text-muted-foreground">Taking you to your dashboard.</p>
                </div>
              )}
              {status === 'error' && (
                <div className="flex flex-col space-y-4 py-4">
                  <p className="text-sm text-destructive">{errorMessage}</p>
                  <button
                    onClick={() => navigate('/login', { replace: true })}
                    className="text-sm text-primary underline underline-offset-2 text-left"
                  >
                    Back to sign in
                  </button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </PageLayout>
  )
}
